import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ChevronLeft,
  ChevronRight,
  File,
  FileText,
  HardDrive,
  Image as ImageIcon,
  Images,
  Plus,
  Tag,
  Upload,
} from 'lucide-react';
import {
  createAdminMedia,
  fetchAdminMedia,
  fetchAdminMediaAsset,
  patchAdminMedia,
  type AdminMediaCategory,
  type AdminMediaRow,
} from '../../api/admin';
import { ApiError } from '../../api/client';
import {
  AdminDetailCard,
  AdminEmptyState,
  AdminKpiStrip,
  AdminPageLayout,
  AdminSearchInput,
  AdminStatusBadge,
  AdminTablePanel,
} from '../../components/admin/AdminPageLayout';
import { LoadingState, ErrorState } from '../../components/states/StateViews';

function mimeIcon(mimeType?: string | null) {
  if (!mimeType) return File;
  if (mimeType.startsWith('image/')) return ImageIcon;
  if (mimeType === 'application/pdf') return FileText;
  return File;
}

function sourceLabel(source: AdminMediaRow['source']) {
  return source === 'platform' ? 'Platform' : 'Student vault';
}

function readFileAsBase64(file: File): Promise<{ fileName: string; mimeType: string; fileData: string }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? '');
      const base64 = result.includes(',') ? result.split(',')[1] : result;
      resolve({ fileName: file.name, mimeType: file.type || 'application/octet-stream', fileData: base64 });
    };
    reader.onerror = () => reject(new Error('Could not read file'));
    reader.readAsDataURL(file);
  });
}

function CreateMediaModal({
  open,
  onClose,
  categories,
}: {
  open: boolean;
  onClose: () => void;
  categories: AdminMediaCategory[];
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    name: '',
    categoryId: categories[0]?.id ?? 'photos',
    subcategory: '',
    description: '',
    tags: '',
  });
  const [fileMeta, setFileMeta] = useState<{ fileName: string; mimeType: string; fileData: string } | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setForm({
      name: '',
      categoryId: categories[0]?.id ?? 'photos',
      subcategory: '',
      description: '',
      tags: '',
    });
    setFileMeta(null);
    setError('');
  }, [open, categories]);

  const create = useMutation({
    mutationFn: () =>
      createAdminMedia({
        name: form.name.trim(),
        categoryId: form.categoryId,
        subcategory: form.subcategory.trim(),
        description: form.description.trim() || undefined,
        tags: form.tags
          ? form.tags.split(',').map((tag) => tag.trim()).filter(Boolean)
          : [],
        ...(fileMeta ?? {}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-media'] });
      onClose();
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : 'Failed to upload media');
    },
  });

  if (!open) return null;

  return (
    <div className="wfx-admin-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="wfx-admin-modal glass-surface"
        role="dialog"
        aria-labelledby="create-media-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="wfx-admin-modal-head">
          <div>
            <p className="wfx-admin-breadcrumb">Media library</p>
            <h2 id="create-media-title"><Upload size={20} /> Upload asset</h2>
          </div>
        </header>
        <form
          className="wfx-admin-form"
          onSubmit={(e) => {
            e.preventDefault();
            setError('');
            create.mutate();
          }}
        >
          <label className="wfx-admin-field">
            <span>Name</span>
            <input required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          </label>
          <div className="wfx-admin-form-row">
            <label className="wfx-admin-field">
              <span>Category</span>
              <select value={form.categoryId} onChange={(e) => setForm((f) => ({ ...f, categoryId: e.target.value }))}>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.label}</option>
                ))}
              </select>
            </label>
            <label className="wfx-admin-field">
              <span>Subcategory</span>
              <input required value={form.subcategory} onChange={(e) => setForm((f) => ({ ...f, subcategory: e.target.value }))} />
            </label>
          </div>
          <label className="wfx-admin-field">
            <span>Description</span>
            <textarea
              rows={3}
              className="wfx-admin-textarea"
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            />
          </label>
          <label className="wfx-admin-field">
            <span>Tags (comma-separated)</span>
            <input value={form.tags} placeholder="hero, marketing" onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))} />
          </label>
          <label className="wfx-admin-field">
            <span>File (optional, max 5 MB)</span>
            <input
              type="file"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) {
                  setFileMeta(null);
                  return;
                }
                try {
                  const meta = await readFileAsBase64(file);
                  setFileMeta(meta);
                  if (!form.name.trim()) {
                    setForm((f) => ({ ...f, name: file.name.replace(/\.[^.]+$/, '') }));
                  }
                } catch {
                  setError('Could not read selected file');
                  setFileMeta(null);
                }
              }}
            />
            {fileMeta && (
              <span className="wfx-muted wfx-admin-subline">
                {fileMeta.fileName} · {fileMeta.mimeType}
              </span>
            )}
          </label>
          {error && <p className="wfx-admin-form-error">{error}</p>}
          <div className="wfx-admin-modal-actions">
            <button type="button" className="secondary-action" onClick={onClose}>Cancel</button>
            <button type="submit" className="primary-action" disabled={create.isPending}>
              {create.isPending ? 'Uploading…' : 'Add to library'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function MediaDetailCard({
  mediaId,
  categories,
  onClose,
}: {
  mediaId: string | null;
  categories: AdminMediaCategory[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin-media-item', mediaId],
    queryFn: () => fetchAdminMediaAsset(mediaId!),
    enabled: Boolean(mediaId),
  });

  const update = useMutation({
    mutationFn: (body: Partial<AdminMediaRow>) => patchAdminMedia(mediaId!, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-media'] });
      void qc.invalidateQueries({ queryKey: ['admin-media-item', mediaId] });
    },
  });

  const row = data?.media;
  const Icon = mimeIcon(row?.mimeType);

  return (
    <AdminDetailCard
      open={Boolean(mediaId)}
      onClose={onClose}
      ariaLabel="Media asset details"
      breadcrumb="Asset profile"
      title={row?.name ?? 'Loading…'}
      illustration="admin.module.media"
      subtitle={
        row ? (
          <>
            <span className="chip-pill">{sourceLabel(row.source)}</span>
            {row.expiryStatus && <AdminStatusBadge status={row.expiryStatus} />}
            <span className="wfx-muted wfx-admin-subline">{row.mimeType ?? 'Unknown type'}</span>
          </>
        ) : undefined
      }
    >
      {isLoading && <LoadingState label="Loading asset…" />}
      {isError && <ErrorState message="Could not load asset." onRetry={() => void refetch()} />}

      {row && (
        <>
          <dl className="wfx-admin-meta-list wfx-admin-meta-list--grid">
            <div><dt>Category</dt><dd>{categories.find((c) => c.id === row.categoryId)?.label ?? row.categoryId}</dd></div>
            <div><dt>Subcategory</dt><dd>{row.subcategory}</dd></div>
            <div><dt>File size</dt><dd>{row.fileSize ?? '—'}</dd></div>
            <div><dt>Uploaded</dt><dd>{new Date(row.uploadedAt).toLocaleDateString()}</dd></div>
            <div><dt>Owner</dt><dd>{row.ownerName ?? 'Platform'}{row.ownerEmail ? ` · ${row.ownerEmail}` : ''}</dd></div>
            <div><dt>Version</dt><dd>{row.version}</dd></div>
          </dl>

          <div className="wfx-admin-media-preview">
            <Icon size={48} aria-hidden="true" />
            <span>{row.fileName ?? row.name}</span>
          </div>

          <div className="wfx-admin-drawer-columns">
            <div className="wfx-admin-drawer-section">
              <h3>Metadata</h3>
              <div className="wfx-admin-form">
                <label className="wfx-admin-field">
                  <span>Name</span>
                  <input
                    defaultValue={row.name}
                    onBlur={(e) => {
                      const value = e.target.value.trim();
                      if (value && value !== row.name) update.mutate({ name: value });
                    }}
                  />
                </label>
                <div className="wfx-admin-form-row">
                  <label className="wfx-admin-field">
                    <span>Category</span>
                    <select
                      defaultValue={row.categoryId}
                      onChange={(e) => update.mutate({ categoryId: e.target.value })}
                    >
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>{cat.label}</option>
                      ))}
                    </select>
                  </label>
                  <label className="wfx-admin-field">
                    <span>Subcategory</span>
                    <input
                      defaultValue={row.subcategory}
                      onBlur={(e) => {
                        const value = e.target.value.trim();
                        if (value && value !== row.subcategory) update.mutate({ subcategory: value });
                      }}
                    />
                  </label>
                </div>
                <label className="wfx-admin-field">
                  <span>Description</span>
                  <textarea
                    rows={4}
                    className="wfx-admin-textarea"
                    defaultValue={row.description ?? ''}
                    onBlur={(e) => {
                      const value = e.target.value.trim();
                      if (value !== (row.description ?? '')) update.mutate({ description: value || null });
                    }}
                  />
                </label>
                <label className="wfx-admin-field">
                  <span>Tags (comma-separated)</span>
                  <input
                    defaultValue={row.tags.join(', ')}
                    onBlur={(e) => {
                      const tags = e.target.value
                        .split(',')
                        .map((tag) => tag.trim())
                        .filter(Boolean);
                      if (tags.join(',') !== row.tags.join(',')) update.mutate({ tags });
                    }}
                  />
                </label>
              </div>
            </div>

            <div className="wfx-admin-drawer-section">
              <h3>Lifecycle</h3>
              <div className="wfx-admin-form">
                <label className="wfx-admin-field">
                  <span>Expiry date</span>
                  <input
                    type="date"
                    defaultValue={row.expiryDate ?? ''}
                    onBlur={(e) => {
                      const value = e.target.value || null;
                      if (value !== (row.expiryDate ?? null)) update.mutate({ expiryDate: value });
                    }}
                  />
                </label>
                <div className="wfx-admin-meta-list">
                  <div>
                    <dt>Source</dt>
                    <dd>{sourceLabel(row.source)}</dd>
                  </div>
                  {row.userId !== 'platform' && (
                    <div>
                      <dt>Student ID</dt>
                      <dd>{row.userId}</dd>
                    </div>
                  )}
                </div>
                {row.tags.length > 0 && (
                  <div className="wfx-admin-tag-row">
                    {row.tags.map((tag) => (
                      <span key={tag} className="chip-pill"><Tag size={12} aria-hidden="true" /> {tag}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </AdminDetailCard>
  );
}

function MediaCard({
  row,
  categories,
  onSelect,
}: {
  row: AdminMediaRow;
  categories: AdminMediaCategory[];
  onSelect: () => void;
}) {
  const Icon = mimeIcon(row.mimeType);
  const categoryLabel = categories.find((cat) => cat.id === row.categoryId)?.label ?? row.categoryId;

  return (
    <button type="button" className="wfx-admin-media-card glass-surface" onClick={onSelect}>
      <div className="wfx-admin-media-card-icon">
        <Icon size={28} aria-hidden="true" />
      </div>
      <div className="wfx-admin-page-card-copy">
        <strong>{row.name}</strong>
        <span className="wfx-muted">{categoryLabel} · {row.subcategory}</span>
      </div>
      <div className="wfx-admin-page-card-stats">
        <span className="chip-pill">{sourceLabel(row.source)}</span>
        {row.fileSize && <span><HardDrive size={14} aria-hidden="true" /> {row.fileSize}</span>}
        {row.expiryStatus && <AdminStatusBadge status={row.expiryStatus} />}
      </div>
    </button>
  );
}

export function AdminMediaScreen() {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    setPage(1);
  }, [debouncedQuery, categoryFilter, sourceFilter, typeFilter]);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin-media', debouncedQuery, categoryFilter, sourceFilter, typeFilter, page],
    queryFn: () =>
      fetchAdminMedia({
        q: debouncedQuery || undefined,
        categoryId: categoryFilter,
        source: sourceFilter,
        type: typeFilter,
        page,
        limit: 12,
      }),
  });

  const rows = data?.data ?? [];
  const stats = data?.stats;
  const categories = data?.categories ?? [];
  const pagination = data?.pagination;

  const kpiItems = useMemo(
    () => [
      { label: 'Total assets', value: stats?.total ?? 0 },
      { label: 'Platform', value: stats?.platform ?? 0 },
      { label: 'Student uploads', value: stats?.student ?? 0 },
      { label: 'Est. storage', value: `${stats?.estimatedMb ?? 0} MB` },
    ],
    [stats],
  );

  if (isLoading && !data) return <LoadingState label="Loading media…" />;
  if (isError) return <ErrorState message="Could not load media library." onRetry={() => void refetch()} />;

  return (
    <>
      <AdminPageLayout
        breadcrumb="Admin / Media Library"
        title="Media Library"
        description="Browse platform assets and student vault uploads — filter by category, type, and source."
        icon={Images}
        illustration="admin.module.media"
        actions={(
          <button type="button" className="primary-action compact" onClick={() => setCreateOpen(true)}>
            <Plus size={16} aria-hidden="true" />
            Upload asset
          </button>
        )}
      >
        <AdminKpiStrip items={kpiItems} />

        <div className="wfx-admin-blog-grid">
          {rows.map((row) => (
            <MediaCard key={row.id} row={row} categories={categories} onSelect={() => setSelectedId(row.id)} />
          ))}
        </div>
        {rows.length === 0 && <AdminEmptyState message="No media assets match your filters." />}

        <AdminTablePanel
          title="Asset directory"
          toolbar={(
            <div className="wfx-admin-toolbar">
              <AdminSearchInput
                value={query}
                onChange={setQuery}
                placeholder="Search name, category, or filename…"
              />
              <select
                className="wfx-admin-select"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                aria-label="Filter by category"
              >
                <option value="all">All categories</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.label}</option>
                ))}
              </select>
              <select
                className="wfx-admin-select"
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
                aria-label="Filter by source"
              >
                <option value="all">All sources</option>
                <option value="platform">Platform</option>
                <option value="student">Student vault</option>
              </select>
              <select
                className="wfx-admin-select"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                aria-label="Filter by file type"
              >
                <option value="all">All types</option>
                <option value="image">Images</option>
                <option value="pdf">PDF</option>
                <option value="other">Other</option>
              </select>
            </div>
          )}
          footer={
            pagination && pagination.pages > 1 ? (
              <div className="wfx-admin-pagination">
                <button
                  type="button"
                  className="secondary-action compact"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  <ChevronLeft size={16} aria-hidden="true" />
                  Previous
                </button>
                <span className="wfx-muted">
                  Page {pagination.page} of {pagination.pages} · {pagination.total} assets
                </span>
                <button
                  type="button"
                  className="secondary-action compact"
                  disabled={page >= pagination.pages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                  <ChevronRight size={16} aria-hidden="true" />
                </button>
              </div>
            ) : undefined
          }
        >
          <table className="wfx-admin-table">
            <thead>
              <tr><th>Name</th><th>Category</th><th>Type</th><th>Source</th><th>Size</th><th>Uploaded</th></tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.id}
                  className="wfx-admin-table-row-clickable"
                  onClick={() => setSelectedId(row.id)}
                >
                  <td><strong>{row.name}</strong></td>
                  <td>{categories.find((c) => c.id === row.categoryId)?.label ?? row.categoryId}</td>
                  <td>{row.mimeType ?? '—'}</td>
                  <td>{sourceLabel(row.source)}</td>
                  <td>{row.fileSize ?? '—'}</td>
                  <td>{new Date(row.uploadedAt).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </AdminTablePanel>
      </AdminPageLayout>

      <CreateMediaModal open={createOpen} onClose={() => setCreateOpen(false)} categories={categories} />
      <MediaDetailCard mediaId={selectedId} categories={categories} onClose={() => setSelectedId(null)} />
    </>
  );
}
